<div #interactivePlanContainer class="interactive-plan-container">
  <!-- Main Layout Container -->
  <div class="bg-white rounded-lg shadow-lg overflow-hidden">
    <!-- Preview Header -->
    <div class="bg-gray-50 px-6 py-4 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <h4 class="text-lg font-semibold text-gray-800">Plan Vorschau</h4>
        <div class="flex items-center space-x-2">
          <button 
            mat-icon-button 
            size="small" 
            class="text-gray-600 hover:text-gray-800"
            (click)="zoomOut()"
            [disabled]="stage?.scaleX() <= planConfig.minZoom"
            title="Verkleinern">
            <mat-icon>zoom_out</mat-icon>
          </button>
          
          <span class="text-sm font-medium text-gray-700 min-w-[3rem] text-center">
            {{ planState.zoomLevel | number:'1.0-0' }}%
          </span>
          
          <button 
            mat-icon-button 
            size="small" 
            class="text-gray-600 hover:text-gray-800"
            (click)="zoomIn()"
            [disabled]="stage?.scaleX() >= planConfig.maxZoom"
            title="Vergrößern">
            <mat-icon>zoom_in</mat-icon>
          </button>
          
          <!-- Fullscreen button hidden for now -->
          <!-- <button 
            mat-icon-button 
            size="small" 
            class="text-gray-600 hover:text-gray-800"
            (click)="toggleFullscreen()"
            [title]="isFullscreen ? 'Vollbild beenden' : 'Vollbild'">
            <mat-icon>{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</mat-icon>
          </button> -->
        </div>
      </div>
    </div>

    <!-- Preview Content -->
    <div class="flex h-[600px]">
      <!-- Plan Image Preview Area (Left) -->
      <div class="flex-1 bg-gray-100 p-6">
        <div class="h-full bg-white rounded-lg shadow-inner border-2 border-dashed border-gray-300 flex items-center justify-center">
          <!-- Konva Canvas Container -->
          <div 
            *ngIf="planImageUrl"
            #konvaContainer 
            class="konva-canvas w-full h-full">
          </div>
          
          <!-- Fallback when no image available -->
          <div *ngIf="!planImageUrl" class="text-center">
            <mat-icon class="text-gray-400 text-6xl mb-4">picture_as_pdf</mat-icon>
            <h5 class="text-lg font-medium text-gray-700 mb-2">Plan Vorschau</h5>
            <p class="text-gray-500 text-sm">Kein Bild verfügbar</p>
          </div>
        </div>
      </div>

      <!-- Indicators Sidebar (Right) -->
      <div class="w-80 bg-gray-50 border-l border-gray-200 flex flex-col">
        <!-- Scrollable Indicators List -->
        <div class="flex-1 overflow-y-auto px-4 pb-4">
          <div class="space-y-2">
            <div *ngFor="let indicator of getSortedRoomIndicators(); let i = index" 
                 class="rounded-lg p-2 shadow-sm border border-gray-200 cursor-pointer transition-all duration-200"
                 [class.bg-white]="hoveredIndicatorId !== indicator.id"
                 [class.bg-gray-50]="hoveredIndicatorId === indicator.id"
                 [class.shadow-md]="hoveredIndicatorId === indicator.id"
                 [attr.data-indicator-id]="indicator.id"
                 [attr.draggable]="false"
                 (mousedown)="onMouseDown($event, indicator)"
                 (click)="onClick($event, indicator)"
                 (dragstart)="onDragStart($event, indicator)"
                 (mouseenter)="hoveredIndicatorId = indicator.id"
                 (mouseleave)="hoveredIndicatorId = null">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-2 flex-1 min-w-0">
                  <!-- Indikator Symbol with Color and Badge -->
                  <div class="w-6 h-6 flex-shrink-0 relative">
                    <!-- Symbol -->
                    <ng-container *ngIf="getIndicatorSymbolUrl(indicator)">
                      <div *ngIf="getIndicatorSymbolColor(indicator); else noColorSymbol"
                           class="w-full h-full"
                           [style.mask-image]="'url(' + getIndicatorSymbolUrl(indicator) + ')'"
                           [style.-webkit-mask-image]="'url(' + getIndicatorSymbolUrl(indicator) + ')'"
                           [style.mask-size]="'contain'"
                           [style.mask-repeat]="'no-repeat'"
                           [style.mask-position]="'center'"
                           [style.background-color]="getIndicatorSymbolColor(indicator)">
                      </div>
                      <ng-template #noColorSymbol>
                        <img [src]="getIndicatorSymbolUrl(indicator)" 
                             [alt]="indicator.indikator?.code || 'Symbol'"
                             class="w-full h-full object-contain">
                      </ng-template>
                    </ng-container>
                    <mat-icon *ngIf="!getIndicatorSymbolUrl(indicator)" class="text-sm text-gray-400">help_outline</mat-icon>
                    <!-- Number Badge below symbol -->
                    <span class="absolute -bottom-2 left-1/2 transform -translate-x-1/2 translate-y-full inline-flex items-center justify-center px-2 py-1 rounded text-xs font-bold text-white bg-blue-500 border border-white shadow-sm whitespace-nowrap"
                          style="min-width: 36px; height: 20px;">
                      {{ getFormattedIndicatorNumber(indicator.indikator_number) }}
                    </span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-800 leading-none mb-0.5">
                      {{ indicator.name }}
                    </p>
                    <p class="text-xs text-gray-500 leading-none mb-0.5">{{ indicator.room?.name || '-' }}</p>
                    <p class="text-xs text-gray-400 leading-none mb-0.5" *ngIf="indicator.indikator?.code">
                      Typ/Code: {{ indicator.indikator.code }}
                    </p>
                    <p class="text-xs text-gray-400 leading-none" *ngIf="indicator.system_id">
                      System-ID: {{ indicator.system_id }}
                    </p>
                  </div>
                </div>
                <div class="text-right flex-shrink-0 ml-2 flex flex-col items-end">
                  <p class="text-xs text-gray-500 leading-none mb-0.5">SW: {{ formatSchwellenwertAsInteger(indicator.schwellenwert) }} {{ indicator.einheit }}</p>
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mb-0.5"
                        [ngClass]="getIndicatorStatusBadgeClasses(indicator.status)">
                    {{ getIndicatorStatusTextSimple(indicator.status) }}
                  </span>
                  <button 
                    type="button"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white text-gray-900 border border-gray-300 hover:bg-gray-50 transition-colors"
                    (click)="openIndicatorDetailDialog(indicator); $event.stopPropagation()"
                    title="Details anzeigen">
                    Detail
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Empty state -->
            <div *ngIf="!roomIndicators || roomIndicators.length === 0" class="text-center py-8">
              <mat-icon class="text-gray-400 text-4xl mb-2">sensors_off</mat-icon>
              <p class="text-sm text-gray-500">Keine Raum-Indikatoren verfügbar</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Preview Footer with Download Button -->
    <div class="bg-gray-50 px-6 py-4 border-t border-gray-200">
      <div class="flex justify-end space-x-2">
        <button 
          type="button"
          mat-stroked-button 
          size="small"
          (click)="saveInteractivePlan(); $event.stopPropagation()"
          [disabled]="!canSaveInteractivePlan()"
          title="Plan mit Indikatoren speichern">
          <mat-icon>save</mat-icon>
          Speichern
        </button>
        <button 
          mat-stroked-button 
          size="small"
          (click)="exportAsImage('jpeg')"
          title="Plan herunterladen">
          <mat-icon>download</mat-icon>
          Download Plan
        </button>
      </div>
    </div>
  </div>

  <!-- Tooltip -->
  <div 
    *ngIf="planState.showTooltip && planState.tooltipMarker" 
    class="tooltip"
    [style.left.px]="planState.tooltipPosition.x + 10"
    [style.top.px]="planState.tooltipPosition.y - 10"
    (click)="hideTooltip()">
    
    <div class="tooltip-content">
      <div class="tooltip-header">
        <div class="tooltip-symbol">
          <ng-container *ngIf="getIndicatorSymbolUrl(planState.tooltipMarker.indicator)">
            <div *ngIf="getIndicatorSymbolColor(planState.tooltipMarker.indicator); else noColorSymbol"
                 class="w-8 h-8"
                 [style.mask-image]="'url(' + getIndicatorSymbolUrl(planState.tooltipMarker.indicator) + ')'"
                 [style.-webkit-mask-image]="'url(' + getIndicatorSymbolUrl(planState.tooltipMarker.indicator) + ')'"
                 [style.mask-size]="'contain'"
                 [style.mask-repeat]="'no-repeat'"
                 [style.mask-position]="'center'"
                 [style.background-color]="getIndicatorSymbolColor(planState.tooltipMarker.indicator)">
            </div>
            <ng-template #noColorSymbol>
              <img [src]="getIndicatorSymbolUrl(planState.tooltipMarker.indicator)" 
                   [alt]="planState.tooltipMarker.indicator.indikator?.code || 'Symbol'"
                   class="w-8 h-8 object-contain">
            </ng-template>
          </ng-container>
        </div>
        <div class="tooltip-info">
          <div class="tooltip-number">
            #{{ getFormattedIndicatorNumber(planState.tooltipMarker.indicator.indikator_number) }}
          </div>
          <div class="tooltip-type">
            {{ planState.tooltipMarker.indicator.indikator?.code || 'Unbekannt' }}
          </div>
        </div>
      </div>
      
      <div class="tooltip-body">
        <div class="tooltip-name">
          <strong>Name:</strong> {{ planState.tooltipMarker.indicator.name }}
        </div>
        <div class="tooltip-room">
          <strong>Raum:</strong> {{ planState.tooltipMarker.indicator.room?.name || '-' }}
        </div>
        <div class="tooltip-room" *ngIf="planState.tooltipMarker.indicator.room?.sektion">
          <strong>Sektion:</strong> {{ planState.tooltipMarker.indicator.room.sektion.name || '-' }}
        </div>
        <div class="tooltip-systemtyp" *ngIf="planState.tooltipMarker.indicator.systemtyp_caption">
          <strong>Systemtyp:</strong> {{ planState.tooltipMarker.indicator.systemtyp_caption }}
        </div>
        <div class="tooltip-position">
          <strong>Position:</strong> X: {{ Math.round(planState.tooltipMarker.x) }}, Y: {{ Math.round(planState.tooltipMarker.y) }}
        </div>
        <div class="tooltip-values">
          <div class="tooltip-schwellenwert">
            <strong>Schwellenwert:</strong> {{ formatSchwellenwertAsInteger(planState.tooltipMarker.indicator.schwellenwert) }} {{ planState.tooltipMarker.indicator.einheit }}
          </div>
          <div class="tooltip-current">
            <strong>Aktueller Wert:</strong> {{ planState.tooltipMarker.indicator.current_value }} {{ planState.tooltipMarker.indicator.einheit }}
          </div>
        </div>
      </div>
      
      <div class="tooltip-footer">
        Klicken zum Schließen
      </div>
    </div>
  </div>
</div>
